import { useEffect, useRef } from 'react';

import type { DesktopRequiredPayload } from '../../../contracts';
import { DESKTOP_APP_DOWNLOAD_URL, useSubAgentComputerUseProgress } from '../../../data';
import { agentHarnessClient } from '../../agent-harness/client';
import { streamHeaders } from '../../agent-harness/stream/stream-http';
import {
  ComputerUseTaskBlock,
  type ComputerUseTaskBlockProps,
} from './ComputerUseTaskBlock';
import type { ComputerUseToolUseViewHost } from './ComputerUseTaskBlock.host';
import { DesktopRequiredBlock } from './DesktopRequiredBlock';

type ComputerUseToolUseViewProps = Omit<
  ComputerUseTaskBlockProps,
  'onApprove' | 'onDeny' | 'host'
> & {
  /** Harness tool_call_id — scopes desktop-required analytics. */
  toolCallId?: string;
  onApprove?: () => void | Promise<void>;
  onDeny?: () => void | Promise<void>;
  /** Typed BE miss — split continue-on-desktop vs already-failed scheduled copy. */
  desktopRequired?: DesktopRequiredPayload | null;
  /** The task card's host, plus the desktop-required friction emit. */
  host: ComputerUseToolUseViewHost;
};

/**
 * Mid-chat ``run_computer_use_task`` UI.
 *
 * A typed ``desktop_required`` on-demand miss is owned by the composer
 * {@link ComputerUseNudgeCard}; this view renders nothing for that case.
 * Schedule/event misses still use {@link DesktopRequiredBlock} in the timeline.
 */
// childAgentRunId stays in the card props: the running body needs it for
// queued-run detection and to gate the inline cloud live view on the run's
// actual target.
export function ComputerUseToolUseView(
  props: ComputerUseToolUseViewProps
): JSX.Element | null {
  if (props.childAgentRunId) {
    return <SubAgentComputerUseCard {...props} childAgentRunId={props.childAgentRunId} />;
  }
  return <ComputerUseToolUseCardView {...props} />;
}

const SubAgentComputerUseCard = ({
  childAgentRunId,
  ...cardProps
}: ComputerUseToolUseViewProps & { childAgentRunId: string }): JSX.Element | null => {
  const progress = useSubAgentComputerUseProgress(childAgentRunId, agentHarnessClient, {
    streamHeaders,
  });
  // The task card owns the run from startup through completion. Child messages
  // supply progress, never a second transcript containing the delegation prompt.
  return (
    <ComputerUseToolUseCardView
      {...cardProps}
      childAgentRunId={childAgentRunId}
      trace={cardProps.trace?.length ? cardProps.trace : progress.trace}
      liveStepCount={cardProps.liveStepCount ?? progress.liveStepCount}
      childRunApproval={
        cardProps.status === 'running'
          ? (cardProps.childRunApproval ?? progress.childRunApproval)
          : undefined
      }
    />
  );
};

function ComputerUseToolUseCardView({
  toolCallId,
  onApprove,
  onDeny,
  desktopRequired,
  host,
  ...blockProps
}: ComputerUseToolUseViewProps): JSX.Element | null {
  const trackedInvokeRef = useRef<string | null>(null);
  // A module-level host, so this is one identity for the life of the app.
  const { onTrackDesktopRequiredShown } = host;

  useEffect(() => {
    if (!desktopRequired || desktopRequired.trigger === 'on_demand') return;
    const key = `typed:${toolCallId ?? 'desktop-required'}`;
    if (trackedInvokeRef.current === key) return;
    trackedInvokeRef.current = key;
    onTrackDesktopRequiredShown({
      surface: 'project_chat',
      blockedAction: 'tool_result',
    });
  }, [desktopRequired, onTrackDesktopRequiredShown, toolCallId]);

  if (desktopRequired?.trigger === 'on_demand') {
    return null;
  }

  if (desktopRequired) {
    return (
      <DesktopRequiredBlock
        payload={desktopRequired}
        downloadHref={DESKTOP_APP_DOWNLOAD_URL}
      />
    );
  }

  return (
    <ComputerUseTaskBlock
      {...blockProps}
      onApprove={onApprove}
      onDeny={onDeny}
      host={host}
    />
  );
}
